import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import {
  Card,
  Stack,
  Button,
  Popover,
  MenuItem,
  Container,
  Typography,
  IconButton,
  Box,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { ArrowUpward, ArrowDownward, MoreHoriz, OpenInNew } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGenerator } from 'utils/apiHelpers';
import { useNavigate, useParams } from 'react-router-dom';
import LessonFormModal from 'components/LessonFormModal/LessonFormModal';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';

export default function LessonCategoryShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [open, setOpen] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [refetchCount, setRefetchCount] = useState(0);
  const [showLessonFormModal, setShowLessonFormModal] = useState(false);

  const { data: lessonCategoryData, isLoading } = useQuery({
    queryKey: ['lessons', refetchCount, id],
    queryFn: () => apiGenerator('get')(`/admin/lesson-categories/${id}`),
  });

  const { lessons, title } = lessonCategoryData?.data?.attributes || {};

  const destroyLessonMutation = useMutation({
    mutationFn: (id) => apiGenerator('delete')(`/admin/lessons/${id}`),
    onSuccess: () => {
      setRefetchCount((prev) => prev + 1);
    },
  });

  const increaseOrderMutation = useMutation({
    mutationFn: (id) => apiGenerator('put')(`/admin/lessons/${id}/increase-order`),
    onSuccess: () => {
      setRefetchCount((prev) => prev + 1);
    },
  });

  const decreaseOrderMutation = useMutation({
    mutationFn: (id) => apiGenerator('put')(`/admin/lessons/${id}/decrease-order`),
    onSuccess: () => {
      setRefetchCount((prev) => prev + 1);
    },
  });

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const rows =
    lessons?.map((lesson) => ({
      id: lesson.id,
      order: lesson.attributes.order,
      title: lesson.attributes.title,
      description: lesson.attributes.description,
      difficulty: lesson.attributes.difficulty,
      premium: lesson.attributes.premium,
      banner_url: lesson.attributes.banner_url,
      media_url: lesson.attributes.media_url,
      media_type: lesson.attributes.media_type,
      tips: lesson.attributes.tips,
    })) || [];

  const columns = [
    { field: 'id', headerName: 'ID', width: 0 },
    { field: 'order', headerName: 'Order', sortable: false, flex: 0.2 },
    {
      field: 'banner_url',
      headerName: 'Banner',
      renderCell: ({ row }) => (
        <Box sx={{ width: 60, height: 60, borderRadius: 1, overflow: 'hidden' }}>
          <img src={row.banner_url} alt="no-banner" />
        </Box>
      ),
      flex: 0.2,
    },

    {
      field: 'media_url',
      headerName: 'Media',
      renderCell: ({ row }) => (
        <Box sx={{ width: 60, height: 60, borderRadius: 1, overflow: 'hidden' }}>
          {row.media_type?.includes('video') ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video style={{ height: 60 }} src={row.media_url} autoPlay loop muted alt="no-media" />
          ) : (
            <img src={row.media_url} alt="no-media" />
          )}
        </Box>
      ),
      flex: 0.2,
    },
    { field: 'title', headerName: 'Title', flex: 0.2 },
    { field: 'description', headerName: 'Description', flex: 0.2 },
    { field: 'difficulty', headerName: 'Difficulty', flex: 0.2 },
    { field: 'premium', headerName: 'Premium', flex: 0.2 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.3,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display={'flex'}>
          <IconButton
            onClick={() => {
              navigate(`/lessons/${row.id}`);
            }}
          >
            <OpenInNew />
          </IconButton>
          <IconButton
            onClick={() => {
              increaseOrderMutation.mutate(row.id);
            }}
          >
            <ArrowUpward />
          </IconButton>
          <IconButton
            onClick={() => {
              decreaseOrderMutation.mutate(row.id);
            }}
          >
            <ArrowDownward />
          </IconButton>
          <IconButton
            onClick={(e) => {
              setOpen(e.currentTarget);
              setSelectedLesson(row);
            }}
          >
            <MoreHoriz />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Lessons | Petzip</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Category: {title}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => {
              setSelectedLesson(null);
              setShowLessonFormModal(true);
            }}
          >
            New Lesson
          </Button>
        </Stack>

        <Card>
          <Scrollbar>
            <div style={{ height: 400, width: '100%' }}>
              <DataGrid rows={rows} columns={columns} hideFooter rowHeight={100} />
            </div>
          </Scrollbar>
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setOpen(null);
            setShowLessonFormModal(true);
          }}
        >
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            destroyLessonMutation.mutate(selectedLesson.id);
            setOpen(null);
          }}
        >
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      {showLessonFormModal && (
        <LessonFormModal
          open={showLessonFormModal}
          setOpen={setShowLessonFormModal}
          onSuccess={() => {
            setRefetchCount((prev) => prev + 1);
            setOpen(null);
          }}
          lesson={selectedLesson}
          lessonCategoryId={id}
        />
      )}

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
